import React, { useState, useEffect } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  Container, Paper, Typography, Box, Button, Grid, TextField,
  Alert, CircularProgress, Card, CardContent, Chip, Stack,
  Divider, LinearProgress, Snackbar, Collapse
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import HotelIcon from '@mui/icons-material/Hotel';
import FlowersIcon from '@mui/icons-material/EmojiNature';
import MapIcon from '@mui/icons-material/Map';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useParams, useNavigate } from 'react-router-dom';
import { getAssignmentByToken, saveAssignment } from '../../../services/guestAssignmentService';

const LOGO_URL = '/assets2/La-Capialla-Hotel-Vector-scaled.png';

// ─── Tooltip flotante compartido ─────────────────────────────────────────────
function RoomTooltip({ tooltip }) {
  if (!tooltip) return null;
  return (
    <div style={{
      position: 'fixed',
      left: tooltip.x + 14,
      top: tooltip.y - 10,
      background: '#1a1a1a',
      color: '#fff',
      padding: '6px 12px',
      borderRadius: 8,
      fontSize: 12,
      fontFamily: 'sans-serif',
      pointerEvents: 'none',
      zIndex: 9999,
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      whiteSpace: 'nowrap',
      borderLeft: '3px solid #d4af37',
      maxWidth: 220,
    }}>
      <div style={{ fontWeight: 700, color: '#d4af37', marginBottom: 2 }}>{tooltip.roomName}</div>
      {tooltip.guestName
        ? <><span style={{ opacity: 0.7 }}>👤 </span>{tooltip.guestName}</>
        : <span style={{ opacity: 0.6, fontStyle: 'italic' }}>Sin huésped asignado</span>
      }
      {tooltip.guestWhatsapp && (
        <div style={{ opacity: 0.7, marginTop: 2 }}>📱 {tooltip.guestWhatsapp}</div>
      )}
    </div>
  );
}

// ─── Mapa Casa Hotel ──────────────────────────────────────────────────────────
function CasaHotelMap({ rooms }) {
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const getRoomData = (number) => {
    if (!rooms) return null;
    return rooms.find(r => r.number === number || r.name?.includes(`${number}`));
  };

  const getStatus = (number) => {
    const room = getRoomData(number);
    if (!room) return 'empty';
    return room.guestName?.trim() ? 'filled' : 'empty';
  };

  const colors = {
    yellow:  { fill: '#FFF9C4', stroke: '#F9A825', text: '#5D4037' },
    green:   { fill: '#DCEDC8', stroke: '#558B2F', text: '#1B5E20' },
    blue:    { fill: '#BBDEFB', stroke: '#1565C0', text: '#0D47A1' },
    neutral: { fill: '#F5F5F5', stroke: '#BDBDBD', text: '#424242' },
    staff:   { fill: '#EEEEEE', stroke: '#9E9E9E', text: '#616161' },
    path:    { fill: '#E0E0E0', stroke: '#BDBDBD', text: '#757575' },
  };

  const filled = { fill: '#C8E6C9', stroke: '#2E7D32', text: '#1B5E20' };

  const getRoomColors = (number, baseColor) => {
    if (hovered === number) return { fill: '#FFF8E1', stroke: '#d4af37', text: '#4E342E' };
    const status = getStatus(number);
    if (status === 'filled') return { ...filled, stroke: baseColor.stroke };
    return baseColor;
  };

  const handleMouseEnter = (e, number, name) => {
    const room = getRoomData(number);
    setHovered(number);
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      roomName: name,
      guestName: room?.guestName || '',
      guestWhatsapp: room?.guestWhatsapp || '',
    });
  };

  const handleMouseMove = (e) => {
    if (tooltip) setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
  };

  const handleMouseLeave = () => {
    setHovered(null);
    setTooltip(null);
  };

  const Room = ({ x, y, w, h, number, name, colorKey = 'yellow', rx = 4 }) => {
    const base = colors[colorKey];
    const c = getRoomColors(number, base);
    const status = getStatus(number);
    const isSuite = name.includes('*');
    return (
      <g
        style={{ cursor: 'pointer' }}
        onMouseEnter={(e) => handleMouseEnter(e, number, name.replace('*', '') + (isSuite ? ' ★' : ''))}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <rect
          x={x} y={y} width={w} height={h} rx={rx}
          fill={c.fill}
          stroke={hovered === number ? '#d4af37' : c.stroke}
          strokeWidth={hovered === number ? 2 : 1}
          style={{ transition: 'all 0.2s' }}
        />
        {status === 'filled' && (
          <circle cx={x + w - 8} cy={y + 8} r={5} fill="#2E7D32" />
        )}
        <text
          x={x + w / 2} y={y + h / 2 - 7}
          textAnchor="middle" dominantBaseline="central"
          fontSize="11" fontWeight="600"
          fill={c.text} fontFamily="Georgia, serif"
        >
          {name}
        </text>
        <text
          x={x + w / 2} y={y + h / 2 + 8}
          textAnchor="middle" dominantBaseline="central"
          fontSize="9.5" fill={c.text} opacity={0.75} fontFamily="sans-serif"
        >
          ({number})
        </text>
      </g>
    );
  };

  const Label = ({ x, y, text, size = 10, weight = '400', color = '#757575', angle }) => (
    <text
      x={x} y={y}
      textAnchor="middle" dominantBaseline="central"
      fontSize={size} fontWeight={weight} fill={color}
      transform={angle ? `rotate(${angle}, ${x}, ${y})` : undefined}
      fontFamily="sans-serif"
      letterSpacing="0.5"
    >
      {text}
    </text>
  );

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
      <RoomTooltip tooltip={tooltip} />
      <svg width="100%" viewBox="0 0 660 460" xmlns="http://www.w3.org/2000/svg">
        {/* Fondo general */}
        <rect x="0" y="0" width="660" height="460" fill="#FAFAFA" rx="12" />

        {/* Título del mapa */}
        <rect x="20" y="10" width="160" height="28" rx="6" fill="#d4af37" />
        <text x="100" y="24" textAnchor="middle" dominantBaseline="central"
          fontSize="12" fontWeight="700" fill="#fff" fontFamily="Georgia, serif" letterSpacing="0.8">
          CASA HOTEL
        </text>

        {/* ── Fila superior: hab. 6-9 + ÁREA STAFF ── */}
        <Room x={185} y={10}  w={105} h={50} number={6}  name="SAN RAFAEL*"     colorKey="yellow" />
        <Room x={298} y={10}  w={105} h={50} number={7}  name="SANTA GLAFIRA*"  colorKey="green"  />
        <Room x={411} y={10}  w={105} h={50} number={8}  name="SANTA TRINIDAD*" colorKey="green"  />
        <Room x={524} y={10}  w={105} h={50} number={9}  name="SANTA SOCORRO"   colorKey="yellow" />

        {/* AREA STAFF */}
        <rect x={524} y={68} width={105} height={35} rx={4}
          fill={colors.staff.fill} stroke={colors.staff.stroke} strokeWidth={0.5} />
        <text x={576} y={85} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill={colors.staff.text} fontFamily="sans-serif">ÁREA STAFF</text>

        {/* ── Pasillo horizontal ── */}
        <rect x={185} y={68} width={335} height={30} rx={4}
          fill={colors.path.fill} stroke={colors.path.stroke} strokeWidth={0.5} />
        <Label x={352} y={83} text="PASILLO" size={9} color="#9E9E9E" />

        {/* ── Columna izquierda ── */}
        <Room x={30} y={50}  w={100} h={70} number={5}  name="SAN ALEJANDRO*" colorKey="yellow" />
        <Room x={30} y={128} w={100} h={90} number={4}  name="SANTA MARÍA*"   colorKey="yellow" />
        <Room x={30} y={226} w={100} h={70} number={3}  name="SAN CARLOS*"    colorKey="green"  />
        <Room x={30} y={304} w={100} h={80} number={1}  name="SAN JOSÉ"       colorKey="blue"   />

        {/* ── Pasillo vertical izquierdo ── */}
        <rect x={138} y={105} width={38} height={280} rx={4}
          fill={colors.path.fill} stroke={colors.path.stroke} strokeWidth={0.5} />
        <Label x={157} y={245} text="PASILLO" size={8.5} color="#9E9E9E" angle={-90} />

        {/* ── Patio Central ── */}
        <rect x={184} y={105} width={222} height={225} rx={6}
          fill="#F3F8F0" stroke="#A5D6A7" strokeWidth={1} strokeDasharray="6 3" />
        <Label x={295} y={217} text="PATIO CENTRAL" size={11} weight="600" color="#81C784" />

        {/* ── Pasillo vertical derecho ── */}
        <rect x={482} y={105} width={36} height={310} rx={4}
          fill={colors.path.fill} stroke={colors.path.stroke} strokeWidth={0.5} />
        <Label x={500} y={260} text="PASILLO" size={8.5} color="#9E9E9E" angle={-90} />

        {/* ── Columna derecha ── */}
        <Room x={524} y={110} w={105} h={48} number={10} name="SANTA ESTHER"  colorKey="blue"   />
        <Room x={524} y={165} w={105} h={48} number={11} name="SAN JORGE"     colorKey="blue"   />
        <Room x={524} y={220} w={105} h={48} number={12} name="SAN GUILLERMO" colorKey="blue"   />
        <Room x={524} y={275} w={105} h={48} number={13} name="SAN MIGUEL"    colorKey="blue"   />
        <Room x={524} y={330} w={105} h={48} number={14} name="SAN ISIDRO"    colorKey="green"  />
        <Room x={524} y={385} w={105} h={48} number={15} name="SAN PEDRO*"    colorKey="yellow" />

        {/* ── Zona inferior: Salón Chimenea + Lobby ── */}
        <rect x={185} y={337} width={90} height={48} rx={4}
          fill={colors.neutral.fill} stroke={colors.neutral.stroke} strokeWidth={0.5} />
        <text x={230} y={356} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fontWeight="600" fill="#5D4037" fontFamily="sans-serif">SALÓN</text>
        <text x={230} y={369} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#5D4037" fontFamily="sans-serif">CHIMENEA</text>

        <rect x={282} y={337} width={130} height={48} rx={4}
          fill={colors.neutral.fill} stroke={colors.neutral.stroke} strokeWidth={0.5} />
        <text x={347} y={356} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fontWeight="600" fill="#5D4037" fontFamily="sans-serif">LOBBY</text>
        <text x={347} y={369} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#5D4037" fontFamily="sans-serif">CASA HOTEL</text>

        {/* ── Leyenda ── */}
        <rect x={0} y={424} width={660} height={1} fill="#E0E0E0" />

        <rect x={20}  y={432} width={13} height={13} rx={2} fill={filled.fill} stroke={filled.stroke} strokeWidth={1} />
        <circle cx={28} cy={438} r={4} fill="#2E7D32" />
        <text x={38}  y={438} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Con huésped</text>

        <rect x={130} y={432} width={13} height={13} rx={2} fill={colors.blue.fill} stroke={colors.blue.stroke} strokeWidth={1} />
        <text x={149} y={438} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Standard</text>

        <rect x={210} y={432} width={13} height={13} rx={2} fill={colors.green.fill} stroke={colors.green.stroke} strokeWidth={1} />
        <text x={229} y={438} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Junior Suite</text>

        <rect x={310} y={432} width={13} height={13} rx={2} fill={colors.yellow.fill} stroke={colors.yellow.stroke} strokeWidth={1} />
        <text x={329} y={438} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Master Suite</text>
      </svg>
    </Box>
  );
}

// ─── Mapa Hotel Boutique ──────────────────────────────────────────────────────
function HotelBoutiqueMap({ rooms }) {
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const getRoomData = (number) => {
    if (!rooms) return null;
    return rooms.find(r => r.number === number || r.name?.includes(`${number}`));
  };

  const getStatus = (number) => {
    const room = getRoomData(number);
    if (!room) return 'empty';
    return room.guestName?.trim() ? 'filled' : 'empty';
  };

  const colGreen  = { fill: '#DCEDC8', stroke: '#558B2F', text: '#1B5E20' };
  const colBlue   = { fill: '#BBDEFB', stroke: '#1565C0', text: '#0D47A1' };
  const colYellow = { fill: '#FFF9C4', stroke: '#F9A825', text: '#5D4037' };
  const filled    = { fill: '#C8E6C9', stroke: '#2E7D32' };
  const colPath   = { fill: '#E8E8E8', stroke: '#BDBDBD' };

  const getC = (number, base) => {
    if (hovered === number) return { fill: '#FFF8E1', stroke: '#d4af37', text: base.text };
    if (getStatus(number) === 'filled') return { ...filled, text: base.text };
    return base;
  };

  const handleMouseEnter = (e, number, label) => {
    const room = getRoomData(number);
    setHovered(number);
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      roomName: `Hab. ${label}`,
      guestName: room?.guestName || '',
      guestWhatsapp: room?.guestWhatsapp || '',
    });
  };

  const handleMouseMove = (e) => {
    if (tooltip) setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
  };

  const handleMouseLeave = () => {
    setHovered(null);
    setTooltip(null);
  };

  const Room = ({ x, y, w, h, number, label, base, rx = 4 }) => {
    const c = getC(number, base);
    const status = getStatus(number);
    return (
      <g
        style={{ cursor: 'pointer' }}
        onMouseEnter={(e) => handleMouseEnter(e, number, label)}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <rect x={x} y={y} width={w} height={h} rx={rx}
          fill={c.fill}
          stroke={hovered === number ? '#d4af37' : c.stroke}
          strokeWidth={hovered === number ? 2 : 1}
          style={{ transition: 'all 0.2s' }}
        />
        {status === 'filled' && (
          <circle cx={x + w - 8} cy={y + 8} r={5} fill="#2E7D32" />
        )}
        <text x={x + w / 2} y={y + h / 2}
          textAnchor="middle" dominantBaseline="central"
          fontSize={11} fontWeight="600" fill={c.text} fontFamily="sans-serif">
          {label}
        </text>
      </g>
    );
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
      <RoomTooltip tooltip={tooltip} />
      <svg width="100%" viewBox="0 0 680 490" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="680" height="490" fill="#FAFAFA" rx="12" />

        {/* ══ PLANTA BAJA ══ */}
        <rect x={10} y={8} width={135} height={28} rx={6} fill="#d4af37" />
        <text x={77} y={22} textAnchor="middle" dominantBaseline="central"
          fontSize={11} fontWeight="700" fill="#fff" fontFamily="Georgia, serif" letterSpacing="0.8">
          PLANTA BAJA
        </text>
        <text x={77} y={36} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#888" fontFamily="sans-serif">HOTEL BOUTIQUE</text>

        {/* TERRAZA TRASERA */}
        <rect x={155} y={15} width={510} height={30} rx={4}
          fill="#FFF3E0" stroke="#FFB74D" strokeWidth={0.5} />
        <text x={410} y={30} textAnchor="middle" dominantBaseline="central"
          fontSize={9.5} fontWeight="600" fill="#E65100" fontFamily="sans-serif" letterSpacing="0.5">
          TERRAZA TRASERA
        </text>

        {/* Fila superior PB: 101-106 */}
        <Room x={155} y={52} w={170} h={48} number={101} label="101" base={colGreen} />
        <Room x={333} y={52} w={60}  h={48} number={102} label="102" base={colBlue}  />
        <Room x={401} y={52} w={80}  h={48} number={103} label="103" base={colGreen} />
        <Room x={489} y={52} w={80}  h={48} number={104} label="104" base={colGreen} />
        <Room x={577} y={52} w={52}  h={48} number={105} label="105" base={colBlue}  />
        <Room x={637} y={52} w={33}  h={48} number={106} label="106" base={colBlue}  />

        {/* Pasillo horizontal PB */}
        <rect x={155} y={106} width={515} height={28} rx={3}
          fill={colPath.fill} stroke={colPath.stroke} strokeWidth={0.5} />
        <text x={412} y={120} textAnchor="middle" dominantBaseline="central"
          fontSize={8.5} fill="#9E9E9E" fontFamily="sans-serif">PASILLO</text>

        {/* Escaleras PB */}
        <rect x={10} y={106} width={90} height={100} rx={4}
          fill="#ECEFF1" stroke="#90A4AE" strokeWidth={0.5} />
        <text x={55} y={156} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fontWeight="600" fill="#546E7A" fontFamily="sans-serif">ESCALERAS</text>

        {/* Pasillo interno */}
        <rect x={108} y={140} width={60} height={66} rx={3}
          fill={colPath.fill} stroke={colPath.stroke} strokeWidth={0.5} />
        <text x={138} y={173} textAnchor="middle" dominantBaseline="central"
          fontSize={8} fill="#9E9E9E" fontFamily="sans-serif">PASILLO</text>

        {/* Baños lobby */}
        <rect x={175} y={140} width={72} height={66} rx={3}
          fill="#F3E5F5" stroke="#CE93D8" strokeWidth={0.5} />
        <text x={211} y={165} textAnchor="middle" dominantBaseline="central"
          fontSize={8.5} fontWeight="600" fill="#6A1B9A" fontFamily="sans-serif">BAÑOS</text>
        <text x={211} y={178} textAnchor="middle" dominantBaseline="central"
          fontSize={8} fill="#6A1B9A" fontFamily="sans-serif">LOBBY</text>

        {/* Fila inferior PB: 107-110 */}
        <Room x={255} y={140} w={90}  h={66} number={110} label="110" base={colGreen} />
        <Room x={353} y={140} w={90}  h={66} number={109} label="109" base={colGreen} />
        <Room x={451} y={140} w={90}  h={66} number={108} label="108" base={colGreen} />
        <Room x={549} y={140} w={101} h={66} number={107} label="107" base={colGreen} />

        {/* Lobby */}
        <rect x={10} y={212} width={238} height={75} rx={4}
          fill="#E8F5E9" stroke="#A5D6A7" strokeWidth={0.5} />
        <text x={129} y={245} textAnchor="middle" dominantBaseline="central"
          fontSize={9.5} fontWeight="600" fill="#2E7D32" fontFamily="sans-serif">LOBBY</text>
        <text x={129} y={259} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#2E7D32" fontFamily="sans-serif">HOTEL BOUTIQUE</text>

        {/* Jardín terraza */}
        <rect x={255} y={212} width={395} height={75} rx={4}
          fill="#F1F8E9" stroke="#AED581" strokeWidth={0.5} strokeDasharray="5 3" />
        <text x={452} y={249} textAnchor="middle" dominantBaseline="central"
          fontSize={10} fontWeight="600" fill="#558B2F" fontFamily="sans-serif">JARDÍN TERRAZA CENTRAL</text>

        {/* ══ PLANTA ALTA ══ */}
        <rect x={10} y={302} width={135} height={28} rx={6} fill="#8D6E63" />
        <text x={77} y={316} textAnchor="middle" dominantBaseline="central"
          fontSize={11} fontWeight="700" fill="#fff" fontFamily="Georgia, serif" letterSpacing="0.8">
          PLANTA ALTA
        </text>
        <text x={77} y={330} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#D7CCC8" fontFamily="sans-serif">HOTEL BOUTIQUE</text>

        {/* Fila superior PA: 200-205 */}
        <Room x={155} y={298} w={82} h={50} number={200} label="200" base={colGreen} />
        <Room x={245} y={298} w={82} h={50} number={201} label="201" base={colGreen} />
        <Room x={335} y={298} w={82} h={50} number={202} label="202" base={colGreen} />
        <Room x={425} y={298} w={82} h={50} number={203} label="203" base={colGreen} />
        <Room x={515} y={298} w={82} h={50} number={204} label="204" base={colGreen} />
        <Room x={605} y={298} w={65} h={50} number={205} label="205" base={colGreen} />

        {/* Escaleras PA */}
        <rect x={10} y={354} width={90} height={32} rx={3}
          fill="#ECEFF1" stroke="#90A4AE" strokeWidth={0.5} />
        <text x={55} y={370} textAnchor="middle" dominantBaseline="central"
          fontSize={8.5} fontWeight="600" fill="#546E7A" fontFamily="sans-serif">ESCALERAS</text>

        {/* Pasillo PA */}
        <rect x={108} y={354} width={562} height={32} rx={3}
          fill={colPath.fill} stroke={colPath.stroke} strokeWidth={0.5} />
        <text x={389} y={370} textAnchor="middle" dominantBaseline="central"
          fontSize={8.5} fill="#9E9E9E" fontFamily="sans-serif">PASILLO</text>

        {/* Fila inferior PA: 206-210 — todas amarillas Master Suite */}
        <Room x={155} y={394} w={100} h={48} number={210} label="210 ★" base={colYellow} />
        <Room x={263} y={394} w={100} h={48} number={209} label="209 ★" base={colYellow} />
        <Room x={371} y={394} w={100} h={48} number={208} label="208 ★" base={colYellow} />
        <Room x={479} y={394} w={100} h={48} number={207} label="207 ★" base={colYellow} />
        <Room x={585} y={394} w={85}  h={48} number={206} label="206 ★" base={colYellow} />

        {/* ── Leyenda ── */}
        <rect x={0} y={454} width={680} height={1} fill="#E0E0E0" />

        <rect x={10}  y={462} width={13} height={13} rx={2} fill={filled.fill} stroke={filled.stroke} strokeWidth={1} />
        <circle cx={18} cy={468} r={4} fill="#2E7D32" />
        <text x={29}  y={468} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Con huésped</text>

        <rect x={120} y={462} width={13} height={13} rx={2} fill={colBlue.fill}   stroke={colBlue.stroke}   strokeWidth={1} />
        <text x={139} y={468} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Standard (Queen)</text>

        <rect x={270} y={462} width={13} height={13} rx={2} fill={colGreen.fill}  stroke={colGreen.stroke}  strokeWidth={1} />
        <text x={289} y={468} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Junior Suite (King)</text>

        <rect x={420} y={462} width={13} height={13} rx={2} fill={colYellow.fill} stroke={colYellow.stroke} strokeWidth={1} />
        <text x={439} y={468} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Master Suite ★ (4 pax)</text>
      </svg>
    </Box>
  );
}

// ─── MapSection wrapper ───────────────────────────────────────────────────────
function MapSection({ title, icon: Icon, children }) {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ mb: 3 }}>
      <Button
        fullWidth variant="outlined"
        onClick={() => setOpen(o => !o)}
        startIcon={<Icon sx={{ color: '#d4af37' }} />}
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        sx={{
          justifyContent: 'space-between',
          borderColor: '#d4af37', color: '#1a1a1a',
          fontFamily: 'Georgia, serif', fontWeight: '600',
          py: 1.5, px: 2.5, borderRadius: 2, textTransform: 'none', fontSize: '15px',
          '&:hover': { borderColor: '#b8962e', bgcolor: '#fffbf0' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MapIcon sx={{ fontSize: 18, color: '#d4af37' }} />
          {title}
        </Box>
      </Button>

      <Collapse in={open}>
        <Paper elevation={0} sx={{
          mt: 1, p: 2, border: '1px solid #f0e6c8',
          borderRadius: 2, bgcolor: '#fdfaf4', overflow: 'hidden'
        }}>
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5, fontStyle: 'italic' }}>
            Pasa el cursor sobre una habitación para ver quién está asignado. Verde oscuro indica que ya tiene huésped.
          </Typography>
          {children}
        </Paper>
      </Collapse>
    </Box>
  );
}

// ─── TabPanel ─────────────────────────────────────────────────────────────────
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index}
      id={`guest-tabpanel-${index}`} aria-labelledby={`guest-tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function GuestAssignmentForm() {
  const { token } = useParams();
  const navigate  = useNavigate();

  const [assignment,       setAssignment]       = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [error,            setError]            = useState(null);
  const [success,          setSuccess]          = useState(false);
  const [successMsg,       setSuccessMsg]       = useState('');
  const [tabValue,         setTabValue]         = useState(0);
  const [roomFilter,       setRoomFilter]       = useState('all');
  const [roomFilterLocked, setRoomFilterLocked] = useState(false);

  const [casaHotelRooms, setCasaHotelRooms] = useState([]);
  const [boutiqueRooms,  setBoutiqueRooms]  = useState([]);

  const filteredCasaHotelRooms = roomFilter === 'all' || roomFilter === 'casa'     ? casaHotelRooms : [];
  const filteredBoutiqueRooms  = roomFilter === 'all' || roomFilter === 'boutique' ? boutiqueRooms  : [];

  const [brideInfo, setBrideInfo] = useState({ name: '', email: '', phone: '' });

  useEffect(() => {
    const loadAssignment = async () => {
      try {
        setLoading(true);
        const data = await getAssignmentByToken(token);
        setAssignment(data.data);

        if (data.data.casaHotelRooms) setCasaHotelRooms(data.data.casaHotelRooms);
        if (data.data.boutiqueRooms)  setBoutiqueRooms(data.data.boutiqueRooms);

        setBrideInfo({
          name:  data.data.brideName  || '',
          email: data.data.brideEmail || '',
          phone: data.data.bridePhone || ''
        });

        if (data.data.hotelType === 'casa' || data.data.hotelType === 'boutique') {
          setRoomFilter(data.data.hotelType);
          setRoomFilterLocked(true);
        } else {
          setRoomFilter('all');
          setRoomFilterLocked(true);
        }
      } catch (err) {
        setError(err.message || 'Error al cargar el formulario');
      } finally {
        setLoading(false);
      }
    };
    if (token) loadAssignment();
  }, [token]);

  const handleRoomFilter = (event, newFilter) => {
    if (newFilter) setRoomFilter(newFilter);
    if (newFilter === 'casa'     && tabValue !== 0) setTabValue(0);
    if (newFilter === 'boutique' && tabValue !== 1) setTabValue(1);
    if (newFilter === 'all'      && tabValue > 1)   setTabValue(0);
  };

  const updateCasaHotelRoom = (index, field, value) => {
    const updated = [...casaHotelRooms];
    updated[index] = { ...updated[index], [field]: value };
    setCasaHotelRooms(updated);
  };

  const updateBoutiqueRoom = (index, field, value) => {
    const updated = [...boutiqueRooms];
    updated[index] = { ...updated[index], [field]: value };
    setBoutiqueRooms(updated);
  };

  const handleBrideInfoChange = (field, value) =>
    setBrideInfo(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const roomsToSaveCasa     = filteredCasaHotelRooms;
      const roomsToSaveBoutique = filteredBoutiqueRooms;

      const filledCount = [...roomsToSaveCasa, ...roomsToSaveBoutique]
        .filter(room => room.guestName?.trim()).length;

      if (filledCount === 0)      throw new Error('Debes asignar al menos un huesped');
      if (!brideInfo.name.trim()) throw new Error('Por favor ingresa tu nombre');
      if (brideInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(brideInfo.email))
        throw new Error('Por favor ingresa un email valido');

      const data = {
        casaHotelRooms: roomsToSaveCasa.map(room => ({
          roomId: room.roomId,
          guestName: room.guestName || '',
          guestWhatsapp: room.guestWhatsapp || '',
          nights: room.nights || 1,
          customPrice: room.customPrice || null
        })),
        boutiqueRooms: roomsToSaveBoutique.map(room => ({
          roomId: room.roomId,
          guestName: room.guestName || '',
          guestWhatsapp: room.guestWhatsapp || '',
          nights: room.nights || 1,
          customPrice: room.customPrice || null
        })),
        brideInfo
      };

      const result = await saveAssignment(token, data);

      // ── Sin navegación: solo mostrar mensaje de éxito ──
      setSuccessMsg(
        `¡Guardado! ${result.data.stats.filledRooms} de ${result.data.stats.totalRooms} habitaciones asignadas`
      );
      setSuccess(true);

    } catch (err) {
      setError(err.message || 'Error al guardar las asignaciones');
    } finally {
      setSaving(false);
    }
  };

  const calculateStats = () => {
    const allRooms     = [...filteredCasaHotelRooms, ...filteredBoutiqueRooms];
    const totalRooms   = allRooms.length;
    const filledRooms  = allRooms.filter(r => r.guestName?.trim()).length;
    const withWhatsapp = allRooms.filter(r => r.guestWhatsapp?.trim()).length;
    return {
      totalRooms, filledRooms, withWhatsapp,
      percentage: totalRooms > 0 ? Math.round((filledRooms / totalRooms) * 100) : 0
    };
  };

  // ── Loading / Error / Empty states ──
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column">
          <CircularProgress sx={{ color: '#d4af37' }} />
          <Typography sx={{ mt: 2, color: '#666666' }}>Cargando tu formulario...</Typography>
        </Box>
      </Container>
    );
  }

  if (error && !assignment) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/')}
          sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333333' } }}>
          Volver al inicio
        </Button>
      </Container>
    );
  }

  if (!assignment) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="warning">No se encontró el formulario de asignación</Alert>
        <Button variant="contained" onClick={() => navigate('/')}
          sx={{ mt: 2, bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333333' } }}>
          Volver al inicio
        </Button>
      </Container>
    );
  }

  const stats      = calculateStats();
  const totalRooms = stats.totalRooms;

  const renderRoomSection = (rooms, title, isCasaHotel = true) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 2,
        color: '#1a1a1a', fontFamily: 'Georgia, serif'
      }}>
        {isCasaHotel
          ? <HomeIcon  sx={{ color: '#d4af37' }} />
          : <HotelIcon sx={{ color: '#d4af37' }} />}
        {title}
        <Chip label={`${rooms.length} habitaciones`} size="small"
          sx={{ bgcolor: '#f5f5f5', color: '#666666', border: '1px solid #e0e0e0' }} />
      </Typography>

      <Grid container spacing={2}>
        {rooms.map((room, index) => (
          <Grid item xs={12} key={room.roomId}>
            <Card variant="outlined" sx={{
              border: '1px solid #e8e8e8', borderRadius: 2, transition: 'all 0.2s ease',
              '&:hover': { borderColor: '#d4af37', boxShadow: '0 2px 8px rgba(212,175,55,0.15)' }
            }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="600"
                        sx={{ color: '#1a1a1a', fontFamily: 'Georgia, serif' }}>
                        {room.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip label={`#${room.number}`} size="small"
                          sx={{ bgcolor: '#1a1a1a', color: '#fff', fontWeight: '500' }} />
                        <Chip label={room.bed} size="small"
                          sx={{ bgcolor: '#f5f5f5', color: '#666', border: '1px solid #e0e0e0' }} />
                        <Chip label={`${room.capacity} pers.`} size="small"
                          sx={{ bgcolor: '#fff', color: '#d4af37', border: '1px solid #d4af37', fontWeight: '500' }} />
                      </Stack>
                      {room.description && (
                        <Typography variant="caption" color="text.secondary"
                          sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                          {room.description}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth label="Nombre del Huesped"
                          value={room.guestName || ''}
                          onChange={(e) =>
                            isCasaHotel
                              ? updateCasaHotelRoom(index, 'guestName', e.target.value)
                              : updateBoutiqueRoom(index, 'guestName', e.target.value)
                          }
                          placeholder="Escribe el nombre completo"
                          InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: '#d4af37' }} /> }}
                          sx={{ '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#d4af37' },
                            '&.Mui-focused fieldset': { borderColor: '#1a1a1a' },
                          }}}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth label="WhatsApp (opcional)"
                          value={room.guestWhatsapp || ''}
                          onChange={(e) =>
                            isCasaHotel
                              ? updateCasaHotelRoom(index, 'guestWhatsapp', e.target.value)
                              : updateBoutiqueRoom(index, 'guestWhatsapp', e.target.value)
                          }
                          placeholder="+52 123 456 7890"
                          InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: '#d4af37' }} /> }}
                          sx={{ '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#d4af37' },
                            '&.Mui-focused fieldset': { borderColor: '#1a1a1a' },
                          }}}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // ── Render principal ──
  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: '#fafafa',
      backgroundImage: 'linear-gradient(to bottom, #fafafa 0%, #f5f5f5 100%)'
    }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>

        {/* Filtro de habitaciones */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={roomFilter} exclusive onChange={handleRoomFilter}
            aria-label="Filtro habitaciones" color="primary" disabled={roomFilterLocked}
          >
            <ToggleButton value="all"      aria-label="Todas">Todo el Hotel</ToggleButton>
            <ToggleButton value="casa"     aria-label="Casa Hotel">Solo Casa Hotel</ToggleButton>
            <ToggleButton value="boutique" aria-label="Boutique">Solo Boutique</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Header con logo */}
        <Paper elevation={0} sx={{
          p: 4, mb: 3, bgcolor: '#ffffff',
          border: '1px solid #d4af37', borderRadius: 3, textAlign: 'center'
        }}>
          <Box sx={{ mb: 3 }}>
            <img src={LOGO_URL} alt="Hotel La Capilla"
              style={{ maxWidth: '250px', height: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          </Box>
          <FlowersIcon sx={{ fontSize: 32, color: '#d4af37', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{
            color: '#1a1a1a', fontFamily: 'Georgia, serif', fontWeight: 'bold', letterSpacing: '1px'
          }}>
            {assignment.eventName}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{
            maxWidth: 600, mx: 'auto', mb: 3, fontSize: '15px', lineHeight: 1.8
          }}>
            Con mucho alegría te invitamos a asignar a tus invitados a las habitaciones
            disponibles. Este es un momento especial para organizar juntos este día tan esperado.
          </Typography>
          <Box sx={{ maxWidth: 400, mx: 'auto', mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Progreso de asignaciones</Typography>
              <Typography variant="body2" fontWeight="600" sx={{ color: '#d4af37' }}>
                {stats.filledRooms} de {totalRooms} ({stats.percentage}%)
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={stats.percentage}
              sx={{
                height: 8, borderRadius: 4, bgcolor: '#f0f0f0',
                '& .MuiLinearProgress-bar': { bgcolor: '#d4af37', borderRadius: 4 }
              }}
            />
          </Box>
        </Paper>

        {/* Información de la novia */}
        <Paper elevation={0} sx={{
          p: 4, mb: 4, bgcolor: '#ffffff',
          border: '1px solid #e8e8e8', borderRadius: 3
        }}>
          <Typography variant="h6" gutterBottom sx={{
            color: '#1a1a1a', fontFamily: 'Georgia, serif',
            borderBottom: '1px solid #d4af37', pb: 1, mb: 3
          }}>
            Tu Información
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth required label="Tu Nombre"
                value={brideInfo.name}
                onChange={(e) => handleBrideInfoChange('name', e.target.value)}
                placeholder="Ej: Ana García"
                sx={{ '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#d4af37' },
                  '&.Mui-focused fieldset': { borderColor: '#1a1a1a' },
                }}}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Tu Email" type="email"
                value={brideInfo.email}
                onChange={(e) => handleBrideInfoChange('email', e.target.value)}
                placeholder="ejemplo@email.com"
                sx={{ '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#d4af37' },
                  '&.Mui-focused fieldset': { borderColor: '#1a1a1a' },
                }}}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Tu Teléfono / WhatsApp"
                value={brideInfo.phone}
                onChange={(e) => handleBrideInfoChange('phone', e.target.value)}
                placeholder="+52 123 456 7890"
                sx={{ '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#d4af37' },
                  '&.Mui-focused fieldset': { borderColor: '#1a1a1a' },
                }}}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs para las propiedades */}
        <Paper elevation={0} sx={{
          mb: 4, border: '1px solid #e8e8e8', borderRadius: 3, overflow: 'hidden'
        }}>
          <Grid container>
            {roomFilter === 'all' && (
              <>
                <Grid item xs={12} md={6}>
                  <Box onClick={() => setTabValue(0)} sx={{
                    p: 3, textAlign: 'center', cursor: 'pointer',
                    bgcolor: tabValue === 0 ? '#ffffff' : '#f5f5f5',
                    borderBottom: tabValue === 0 ? '3px solid #d4af37' : '3px solid transparent',
                    transition: 'all 0.2s ease'
                  }}>
                    <HomeIcon sx={{ color: tabValue === 0 ? '#d4af37' : '#999', fontSize: 28, mb: 1 }} />
                    <Typography variant="subtitle1"
                      fontWeight={tabValue === 0 ? '600' : '400'}
                      sx={{ color: tabValue === 0 ? '#1a1a1a' : '#666' }}>
                      Casa Hotel
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {filteredCasaHotelRooms.length} habitaciones disponibles
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box onClick={() => setTabValue(1)} sx={{
                    p: 3, textAlign: 'center', cursor: 'pointer',
                    bgcolor: tabValue === 1 ? '#ffffff' : '#f5f5f5',
                    borderBottom: tabValue === 1 ? '3px solid #d4af37' : '3px solid transparent',
                    transition: 'all 0.2s ease'
                  }}>
                    <HotelIcon sx={{ color: tabValue === 1 ? '#d4af37' : '#999', fontSize: 28, mb: 1 }} />
                    <Typography variant="subtitle1"
                      fontWeight={tabValue === 1 ? '600' : '400'}
                      sx={{ color: tabValue === 1 ? '#1a1a1a' : '#666' }}>
                      Hotel Boutique
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {filteredBoutiqueRooms.length} habitaciones disponibles
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}
            {roomFilter === 'casa' && (
              <Grid item xs={12}>
                <Box sx={{
                  p: 3, textAlign: 'center', bgcolor: '#ffffff',
                  borderBottom: '3px solid #d4af37', transition: 'all 0.2s ease'
                }}>
                  <HomeIcon sx={{ color: '#d4af37', fontSize: 28, mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#1a1a1a' }}>
                    Casa Hotel
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredCasaHotelRooms.length} habitaciones disponibles
                  </Typography>
                </Box>
              </Grid>
            )}
            {roomFilter === 'boutique' && (
              <Grid item xs={12}>
                <Box sx={{
                  p: 3, textAlign: 'center', bgcolor: '#ffffff',
                  borderBottom: '3px solid #d4af37', transition: 'all 0.2s ease'
                }}>
                  <HotelIcon sx={{ color: '#d4af37', fontSize: 28, mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#1a1a1a' }}>
                    Hotel Boutique
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredBoutiqueRooms.length} habitaciones disponibles
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          <Box sx={{ p: 3, bgcolor: '#ffffff' }}>
            {/* ── Casa Hotel ── */}
            {(roomFilter === 'all' && tabValue === 0) && (
              <>
                <MapSection title="Ver mapa de Casa Hotel" icon={HomeIcon}>
                  <CasaHotelMap rooms={casaHotelRooms} />
                </MapSection>
                {renderRoomSection(filteredCasaHotelRooms, 'Casa Hotel', true)}
              </>
            )}
            {roomFilter === 'casa' && (
              <>
                <MapSection title="Ver mapa de Casa Hotel" icon={HomeIcon}>
                  <CasaHotelMap rooms={casaHotelRooms} />
                </MapSection>
                {renderRoomSection(filteredCasaHotelRooms, 'Casa Hotel', true)}
              </>
            )}

            {/* ── Hotel Boutique ── */}
            {(roomFilter === 'all' && tabValue === 1) && (
              <>
                <MapSection title="Ver mapa de Hotel Boutique" icon={HotelIcon}>
                  <HotelBoutiqueMap rooms={boutiqueRooms} />
                </MapSection>
                {renderRoomSection(filteredBoutiqueRooms, 'Hotel Boutique', false)}
              </>
            )}
            {roomFilter === 'boutique' && (
              <>
                <MapSection title="Ver mapa de Hotel Boutique" icon={HotelIcon}>
                  <HotelBoutiqueMap rooms={boutiqueRooms} />
                </MapSection>
                {renderRoomSection(filteredBoutiqueRooms, 'Hotel Boutique', false)}
              </>
            )}
          </Box>
        </Paper>

        {/* Mensajes de error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Snackbar de éxito con mensaje detallado — sin navegación */}
        <Snackbar
          open={success}
          autoHideDuration={5000}
          onClose={() => setSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          message={successMsg || 'Asignaciones guardadas exitosamente'}
          sx={{ '& .MuiSnackbarContent-root': { bgcolor: '#2e7d32', color: '#fff', fontWeight: '500' } }}
        />

        {/* Barra de acciones */}
        <Paper elevation={4} sx={{
          p: 3, position: 'sticky', bottom: 20, zIndex: 1000, borderRadius: 3,
          bgcolor: '#ffffff', border: '1px solid #d4af37'
        }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip
                  icon={<CheckCircleIcon sx={{ color: '#2e7d32 !important' }} />}
                  label={`${stats.filledRooms} de ${totalRooms} asignadas`}
                  sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: '500', border: '1px solid #c8e6c9' }}
                />
                {stats.filledRooms > 0 && (
                  <Chip label={`${stats.percentage}% completado`}
                    sx={{ bgcolor: '#fff9e6', color: '#d4af37', fontWeight: '500', border: '1px solid #ffe0b2' }}
                  />
                )}
              </Stack>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" startIcon={<ArrowBackIcon />}
                  onClick={() => window.history.back()}
                  sx={{ borderColor: '#1a1a1a', color: '#1a1a1a', '&:hover': { borderColor: '#1a1a1a', bgcolor: '#f5f5f5' } }}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || stats.filledRooms === 0}
                  sx={{
                    minWidth: 200, bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' },
                    px: 4, py: 1.5, fontWeight: '500', textTransform: 'none', fontSize: '14px'
                  }}
                >
                  {saving ? 'Guardando...' : 'Guardar Asignaciones'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4, pb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Hotel La Capilla — Tu día especial merece lo mejor
          </Typography>
        </Box>

      </Container>
    </Box>
  );
}